import { useRef, useState, useEffect } from "react";
import useEmblaCarousel from "embla-carousel-react";
import CompanionCard from "./CompanionCard";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollReveal, useStaggerReveal } from "@/hooks/use-scroll-reveal";
import storageImg from "@/assets/companion-storage.webp";
import strapsImg from "@/assets/companion-straps.webp";
import travelImg from "@/assets/companion-travel.webp";

const CATEGORIES = [
  {
    title: "Storage & Display",
    description: "Premium watch winders, storage boxes, and display cases.",
    image: storageImg,
  },
  {
    title: "Premium Watch Straps",
    description: "Hand made straps in fine leather and selected metals.",
    image: strapsImg,
  },
  {
    title: "Travel Cases & Rolls",
    description: "Professional protective watch rolls and travel cases.",
    image: travelImg,
  },
];

const CompanionProducts = () => {
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  const headingRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="pt-12 md:pt-16 lg:pt-20 pb-12 md:pb-16 lg:pb-20">
      <div className="container-site">
        <div ref={headingRef} className="container-content text-center">
          <h2 className="heading-medium text-foreground">
            {t("companion.title")}
          </h2>
          <p className="body-medium text-secondary-foreground mt-3 md:mt-4 max-w-md mx-auto">
            {t("companion.subtitle")}
          </p>
        </div>
      </div>

      <div className="mt-6 md:mt-8 lg:mt-10">
        {isMobile ? <MobileCarousel /> : <DesktopCarousel />}
      </div>

    </section>
  );
};

const MobileCarousel = () => {
  const [emblaRef] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    loop: false,
  });
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <div ref={sectionRef} className="px-4">
      <div ref={emblaRef} className="overflow-hidden">
        <div className="flex gap-4">
          {CATEGORIES.map((p, i) => (
            <div key={i} className="min-w-0 shrink-0 grow-0 basis-[88%]">
              <CompanionCard title={p.title} description={p.description} image={p.image} comingSoon />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const DesktopCarousel = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    loop: false,
    slidesToScroll: 1,
  });

  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const { setRef } = useStaggerReveal(CATEGORIES.length, { preset: "card", interval: 70 });

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => { emblaApi.off("select", onSelect); };
  }, [emblaApi]);

  return (
    <div className="container-site">
      <div className="container-content relative">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex gap-x-8 lg:gap-x-10">
            {CATEGORIES.map((p, i) => (
              <div key={i} ref={setRef(i)} className="min-w-0 shrink-0 grow-0 basis-[calc(33.333%-1.4rem)]">
                <CompanionCard title={p.title} description={p.description} image={p.image} comingSoon />
              </div>
            ))}
          </div>
        </div>

        {canScrollPrev && (
          <button
            onClick={() => emblaApi?.scrollPrev()}
            className="absolute left-[-2rem] top-[40%] -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-background/60 text-foreground/70 hover:text-foreground transition-colors"
            aria-label="Previous"
          >
            ‹
          </button>
        )}
        {canScrollNext && (
          <button
            onClick={() => emblaApi?.scrollNext()}
            className="absolute right-[-2rem] top-[40%] -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-background/60 text-foreground/70 hover:text-foreground transition-colors"
            aria-label="Next"
          >
            ›
          </button>
        )}
      </div>
    </div>
  );
};

export default CompanionProducts;
